import { 
  collection, 
  query, 
  where, 
  getDocs, 
  orderBy, 
  doc, 
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase';
import { format, subDays, startOfDay, endOfDay, isSameDay } from 'date-fns';

/**
 * 팔로우 중인 사용자 ID 목록을 가져옵니다.
 * @param {string} currentUserId - 현재 사용자 ID
 * @returns {Promise<string[]>} 팔로우 중인 사용자 ID 배열
 */
export const getFollowingUserIds = async (currentUserId) => {
  try {
    const followingRef = collection(db, 'users', currentUserId, 'following');
    const followingSnapshot = await getDocs(followingRef);
    return followingSnapshot.docs.map(doc => doc.id);
  } catch (error) {
    console.error('Error fetching following users:', error);
    return [];
  }
};

/**
 * 특정 날짜의 독서 활동을 가져옵니다.
 * @param {Date} date - 조회할 날짜
 * @param {string[]} userIds - 조회할 사용자 ID 배열
 * @param {string} currentUserId - 현재 사용자 ID (우선순위용)
 * @returns {Promise<Array>} 타임라인 아이템 배열
 */
export const fetchTimelineByDate = async (date, userIds, currentUserId = null) => {
  if (userIds.length === 0 && !currentUserId) return [];

  const dateKey = format(date, 'yyyy-MM-dd');
  const startOfDate = startOfDay(date);
  const endOfDate = endOfDay(date);

  try {
    // 현재 사용자와 팔로우한 사용자들의 ID 목록 생성 (현재 사용자가 첫 번째)
    const allUserIds = currentUserId ? [currentUserId, ...userIds] : userIds;
    
    // 각 사용자의 책 데이터를 병렬로 가져오기
    const userBooksPromises = allUserIds.map(async (userId) => {
      const booksRef = collection(db, 'books');
      const booksQuery = query(
        booksRef,
        where('uid', '==', userId)
      );

      const booksSnapshot = await getDocs(booksQuery);
      return booksSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        userId
      }));
    });

    const allUserBooks = await Promise.all(userBooksPromises);
    const flatBooks = allUserBooks.flat();

    // Memo-Edit에 일자가 등록된 책들만 필터링 (다중 기간 지원)
    const validBooks = flatBooks.filter(book => {
      // 새로운 다중 기간 데이터가 있는지 확인
      if (book.readingPeriods && Array.isArray(book.readingPeriods) && book.readingPeriods.length > 0) {
        // 다중 기간 데이터가 있는 경우
        return book.readingPeriods.some(period => {
          if (!period.startDate) return false;
          
          const startDate = period.startDate?.toDate?.() || new Date(period.startDate);
          
          // 읽기 전 상태인 경우: 시작일이 해당 날짜와 정확히 일치하는 경우만 표시
          if (book.readStatus === 'not_started') {
            return isSameDay(startDate, date);
          }
          
          // 읽는 중인 경우: endDate가 있으면 시작일부터 완독일까지, 없으면 시작일만 표시
          if (book.readStatus === 'reading') {
            if (period.endDate) {
              const endDate = period.endDate?.toDate?.() || new Date(period.endDate);
              const startDateOnly = startOfDay(startDate);
              const endDateOnly = startOfDay(endDate);
              const currentDateOnly = startOfDay(date);
              return currentDateOnly >= startDateOnly && currentDateOnly <= endDateOnly;
            } else {
              // endDate가 없는 경우 시작일만 표시
              return isSameDay(startDate, date);
            }
          }
          
          // 완독한 경우: 시작일과 완독일 사이에 해당 날짜가 포함되는지 확인
          if (book.readStatus === 'completed') {
            const startDateOnly = startOfDay(startDate);
            const currentDateOnly = startOfDay(date);
            
            if (period.endDate) {
              const endDate = period.endDate?.toDate?.() || new Date(period.endDate);
              const endDateOnly = startOfDay(endDate);
              return currentDateOnly >= startDateOnly && currentDateOnly <= endDateOnly;
            } else {
              // endDate가 없는 경우 시작일만 표시
              return isSameDay(startDate, date);
            }
          }
          
          return false;
        });
      } else {
        // 기존 단일 기간 데이터 (하위 호환성)
        if (!book.startedAt) {
          return false;
        }
        
        const startedAt = book.startedAt?.toDate?.() || new Date(book.startedAt);
        
        // 읽기 전 상태인 경우: 시작일이 해당 날짜와 정확히 일치하는 경우만 표시
        if (book.readStatus === 'not_started') {
          return isSameDay(startedAt, date);
        }
        
        // 읽는 중인 경우: finishedAt이 있으면 시작일부터 완독일까지, 없으면 시작일만 표시
        if (book.readStatus === 'reading') {
          if (book.finishedAt) {
            const finishedAt = book.finishedAt?.toDate?.() || new Date(book.finishedAt);
            const startDateOnly = startOfDay(startedAt);
            const endDateOnly = startOfDay(finishedAt);
            const currentDateOnly = startOfDay(date);
            return currentDateOnly >= startDateOnly && currentDateOnly <= endDateOnly;
          } else {
            // finishedAt이 없는 경우 시작일만 표시
            return isSameDay(startedAt, date);
          }
        }
        
        // 완독한 경우: 시작일과 완독일 사이에 해당 날짜가 포함되는지 확인
        if (book.readStatus === 'completed') {
          const startDateOnly = startOfDay(startedAt);
          const currentDateOnly = startOfDay(date);
          
          if (book.finishedAt) {
            const finishedAt = book.finishedAt?.toDate?.() || new Date(book.finishedAt);
            const endDateOnly = startOfDay(finishedAt);
            return currentDateOnly >= startDateOnly && currentDateOnly <= endDateOnly;
          } else {
            // finishedAt이 없는 경우 시작일만 표시
            return isSameDay(startedAt, date);
          }
        }
        
        return false;
      }
    });

    // 사용자 정보와 함께 타임라인 아이템 생성 (다중 기간 지원)
    const timelineItems = [];
    
    for (const book of validBooks) {
      const userDoc = await getDoc(doc(db, 'users', book.userId));
      const userData = userDoc.data();
      
      // 새로운 다중 기간 데이터가 있는지 확인
      if (book.readingPeriods && Array.isArray(book.readingPeriods) && book.readingPeriods.length > 0) {
        // 다중 기간 데이터가 있는 경우, 각 기간별로 타임라인 아이템 생성
        for (let i = 0; i < book.readingPeriods.length; i++) {
          const period = book.readingPeriods[i];
          if (!period.startDate) continue;
          
          const startDate = period.startDate?.toDate?.() || new Date(period.startDate);
          
          // 해당 날짜가 이 기간에 포함되는지 확인
          let isInPeriod = false;
          
          if (book.readStatus === 'not_started') {
            isInPeriod = isSameDay(startDate, date);
          } else if (book.readStatus === 'reading' || book.readStatus === 'completed') {
            const startDateOnly = startOfDay(startDate);
            const currentDateOnly = startOfDay(date);
            
            if (period.endDate) {
              const endDate = period.endDate?.toDate?.() || new Date(period.endDate);
              const endDateOnly = startOfDay(endDate);
              isInPeriod = currentDateOnly >= startDateOnly && currentDateOnly <= endDateOnly;
            } else {
              isInPeriod = isSameDay(startDate, date);
            }
          }
          
          if (isInPeriod) {
            timelineItems.push({
              userId: book.userId,
              userDisplayName: userData?.displayName || '익명',
              userPhotoURL: userData?.photoURL || null,
              bookId: book.id,
              bookTitle: book.title,
              bookAuthors: book.authors || [],
              bookThumbnail: book.thumbnail,
              status: book.readStatus,
              startedAt: period.startDate,
              finishedAt: period.endDate,
              updatedAt: book.updatedAt,
              dateKey,
              activityDate: date,
              periodIndex: i, // 기간 인덱스 추가
              // 현재 사용자 여부 (우선순위 정렬용)
              isCurrentUser: book.userId === currentUserId
            });
          }
        }
      } else {
        // 기존 단일 기간 데이터 (하위 호환성)
        timelineItems.push({
          userId: book.userId,
          userDisplayName: userData?.displayName || '익명',
          userPhotoURL: userData?.photoURL || null,
          bookId: book.id,
          bookTitle: book.title,
          bookAuthors: book.authors || [],
          bookThumbnail: book.thumbnail,
          status: book.readStatus,
          startedAt: book.startedAt,
          finishedAt: book.finishedAt,
          updatedAt: book.updatedAt,
          dateKey,
          activityDate: date,
          periodIndex: 0, // 기존 데이터는 첫 번째 기간으로 처리
          // 현재 사용자 여부 (우선순위 정렬용)
          isCurrentUser: book.userId === currentUserId
        });
      }
    }

    // 우선순위 정렬: 현재 사용자 > 팔로우한 사용자
    timelineItems.sort((a, b) => {
      if (a.isCurrentUser && !b.isCurrentUser) return -1;
      if (!a.isCurrentUser && b.isCurrentUser) return 1;
      return 0;
    });

    return timelineItems;
  } catch (error) {
    console.error('Error fetching timeline for date:', dateKey, error);
    return [];
  }
};

/**
 * 날짜 범위의 타임라인 데이터를 가져옵니다.
 * @param {Date} startDate - 시작 날짜
 * @param {Date} endDate - 종료 날짜
 * @param {string[]} userIds - 조회할 사용자 ID 배열
 * @param {boolean} showEmptyDays - 빈 날짜 표시 여부
 * @param {string} currentUserId - 현재 사용자 ID
 * @returns {Promise<Object>} 날짜별 타임라인 데이터
 */
export const fetchTimelineByDateRange = async (startDate, endDate, userIds, showEmptyDays = false, currentUserId = null) => {
  if (userIds.length === 0 && !currentUserId) return {};

  const newTimelineData = {};
  const dates = [];
  
  // 날짜 범위 생성
  let currentDate = new Date(startDate);
  while (currentDate <= endDate) {
    dates.push(new Date(currentDate));
    currentDate = subDays(currentDate, -1);
  }

  // 각 날짜별로 데이터 가져오기 (병렬 처리)
  const datePromises = dates.map(async (date) => {
    const items = await fetchTimelineByDate(date, userIds, currentUserId);
    return { date, items };
  });

  const results = await Promise.all(datePromises);
  
  results.forEach(({ date, items }) => {
    if (items.length > 0 || showEmptyDays) {
      const dateKey = format(date, 'yyyy-MM-dd');
      newTimelineData[dateKey] = items;
    }
  });

  return newTimelineData;
};

/**
 * 독서 활동 집계 데이터를 생성/업데이트합니다.
 * @param {string} userId - 사용자 ID
 * @param {string} bookId - 책 ID
 * @param {string} status - 읽기 상태
 * @param {Object} bookData - 책 데이터
 * @returns {Promise<void>}
 */
export const upsertReadingActivity = async (userId, bookId, status, bookData) => {
  try {
    const dateKey = format(new Date(), 'yyyy-MM-dd');
    const activityId = `${userId}_${bookId}_${dateKey}`;
    
    const activityRef = doc(db, 'readingActivity', activityId);
    
    const activityData = {
      userId,
      bookId,
      dateKey,
      status,
      snapshot: {
        title: bookData.title,
        bookThumb: bookData.thumbnail,
        authors: bookData.authors || []
      },
      sourceUpdatedAt: bookData.updatedAt || serverTimestamp(),
      createdAt: serverTimestamp()
    };

    await setDoc(activityRef, activityData, { merge: true });
  } catch (error) {
    console.error('Error upserting reading activity:', error);
  }
};

/**
 * 사용자의 독서 활동을 집계 컬렉션에 동기화합니다.
 * @param {string} userId - 사용자 ID
 * @returns {Promise<void>}
 */
export const syncUserReadingActivity = async (userId) => {
  try {
    const booksRef = collection(db, 'books');
    const booksQuery = query(
      booksRef,
      where('uid', '==', userId),
      where('readStatus', 'in', ['reading', 'completed']),
      orderBy('updatedAt', 'desc')
    );

    const booksSnapshot = await getDocs(booksQuery);
    
    const syncPromises = booksSnapshot.docs.map(doc => {
      const bookData = { id: doc.id, ...doc.data() };
      return upsertReadingActivity(userId, bookData.id, bookData.readStatus, bookData);
    });

    await Promise.all(syncPromises);
  } catch (error) {
    console.error('Error syncing user reading activity:', error);
  }
};
